import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getHomeRouteByRole } from "../../utils/rutas";
import { formatRelativeDate } from "../../utils/formatters";
import { API_URL } from "../../config";
import { isInstructor } from "../../utils/roles";
import { isSecureHttpsUrl } from "../../utils/isSecureHttpsUrl";
import {
  toggleAllAnnouncementSelection,
  toggleAnnouncementSelection,
} from "../../utils/announcementSelection";
import { filterStaffAnnouncements } from "../../utils/filterStaffAnnouncements";
import { filterAnnouncementsByCourse } from "../../utils/filterAnnouncementsByCourse";
import { announcementToDraftForm } from "../../utils/announcementToDraftForm";
import AnnouncementCourseFilter from "../../components/AnnouncementCourseFilter";
import AnnouncementInteractiveResources from "../../components/AnnouncementInteractiveResources";
import ResponsiveActionSidebar from "../../components/ResponsiveActionSidebar";
import { getMessage } from "../../data/locales";
import { scrollIntoViewSmooth } from "../../utils/scrollIntoViewSmooth";
import ViewportSelect from "../../components/ViewportSelect";
import "../GestionDirector.css";
import "./GestionAnuncios.css";

const EMPTY_LINK = { label: "", url: "" };
const EMPTY_ATTACHMENT = { filename: "", url: "", mimeType: "" };

const GestionAnuncios = () => {
  const navigate = useNavigate();
  const [rol, setRol] = useState("");
  const [userId, setUserId] = useState("");
  const [isRoleLoaded, setIsRoleLoaded] = useState(false);
  const [isRoleError, setIsRoleError] = useState(false);
  const [cursos, setCursos] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [title, setTitle] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [content, setContent] = useState("");
  const [scope, setScope] = useState("global");
  const [courseId, setCourseId] = useState("");
  const [links, setLinks] = useState([{ ...EMPTY_LINK }]);
  const [attachments, setAttachments] = useState([{ ...EMPTY_ATTACHMENT }]);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [feedbackScrollTick, setFeedbackScrollTick] = useState(0);
  const editorHeadingRef = useRef(null);
  const feedbackRef = useRef(null);
  const scrolledFeedbackTickRef = useRef(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [listCourseFilter, setListCourseFilter] = useState("");
  const [editingId, setEditingId] = useState("");

  useEffect(() => {
    if (feedbackScrollTick === 0 || feedbackScrollTick === scrolledFeedbackTickRef.current) {
      return;
    }
    if (!error && !mensaje) {
      return;
    }
    scrolledFeedbackTickRef.current = feedbackScrollTick;
    scrollIntoViewSmooth(feedbackRef.current);
  }, [error, mensaje, feedbackScrollTick]);

  const instructorMode = isInstructor(rol);
  const visibleAnnouncements = useMemo(
    () =>
      filterAnnouncementsByCourse(
        filterStaffAnnouncements(announcements, { userId, rol }),
        listCourseFilter,
      ),
    [announcements, userId, rol, listCourseFilter],
  );

  useEffect(() => {
    const visibleIds = new Set(visibleAnnouncements.map((item) => item._id));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => visibleIds.has(id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [visibleAnnouncements]);

  useEffect(() => {
    if (!listCourseFilter) {
      return;
    }
    const hasSelectedCourse = cursos.some((course) => String(course._id) === listCourseFilter);
    if (!hasSelectedCourse) {
      setListCourseFilter("");
    }
  }, [cursos, listCourseFilter]);

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data && (data.ok || data.usuario) && data.usuario?.rol) {
          const nextRol = data.usuario.rol;
          setRol(nextRol);
          setUserId(String(data.usuario.id || data.usuario._id || ""));
          if (isInstructor(nextRol)) {
            setScope("course");
          }
          setIsRoleLoaded(true);
          return;
        }
        setIsRoleError(true);
        setError("No se pudo verificar tu sesión para publicar comunicados.");
      })
      .catch((err) => {
        console.error("Error obteniendo rol:", err);
        setIsRoleError(true);
        setError("No se pudo verificar tu sesión para publicar comunicados.");
      });
  }, []);

  const loadAnnouncements = useCallback(async () => {
    try {
      const annRes = await fetch(`${API_URL}/announcements/admin`, { credentials: "include" });
      if (annRes.ok) {
        const ann = await annRes.json();
        setAnnouncements(Array.isArray(ann) ? ann : []);
      }
    } catch {
      setError("Error al cargar comunicados");
    }
  }, []);

  const loadCourses = useCallback(async () => {
    try {
      const endpoint = instructorMode
        ? `${API_URL}/announcements/instructor-courses`
        : `${API_URL}/cursos`;
      const res = await fetch(endpoint, { credentials: "include" });
      if (!res.ok) return;
      const datos = await res.json();
      const list = Array.isArray(datos) ? datos : datos?.cursos || [];
      setCursos(list);
      if (instructorMode && list.length === 1) {
        setCourseId(String(list[0]._id));
      }
    } catch {
      setError("Error al cargar cursos");
    }
  }, [instructorMode]);

  useEffect(() => {
    if (!rol) return;
    const loadData = async () => {
      setError("");
      await Promise.all([loadCourses(), loadAnnouncements()]);
    };
    loadData();
  }, [rol, loadCourses, loadAnnouncements]);

  const resetExtras = () => {
    setLinks([{ ...EMPTY_LINK }]);
    setAttachments([{ ...EMPTY_ATTACHMENT }]);
  };

  const clearForm = () => {
    setTitle("");
    setDisplayName("");
    setContent("");
    setCourseId(instructorMode && cursos.length === 1 ? String(cursos[0]._id) : "");
    if (!instructorMode) setScope("global");
    resetExtras();
    setEditingId("");
  };

  const handleStartEdit = (announcement) => {
    if (!announcement || announcement.status !== "draft" || !announcement._id) {
      return;
    }
    const form = announcementToDraftForm(announcement);
    setEditingId(String(announcement._id));
    setTitle(form.title);
    setDisplayName(form.displayName);
    setContent(form.content);
    setScope(instructorMode ? "course" : form.scope);
    setCourseId(form.courseId);
    setLinks(form.links);
    setAttachments(form.attachments);
    setMensaje("");
    setError("");
    scrollIntoViewSmooth(editorHeadingRef.current);
  };

  const buildExtrasPayload = () => {
    const cleanLinks = links
      .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
      .filter((l) => l.label && l.url && isSecureHttpsUrl(l.url));
    const cleanAttachments = attachments
      .map((a) => ({
        filename: a.filename.trim(),
        url: a.url.trim(),
        mimeType: a.mimeType.trim(),
      }))
      .filter((a) => a.filename && a.url && isSecureHttpsUrl(a.url));
    return { links: cleanLinks, attachments: cleanAttachments };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isRoleLoaded || !rol || isRoleError || isSubmitting) return;
    setIsSubmitting(true);
    setMensaje("");
    setError("");
    setFeedbackScrollTick((tick) => tick + 1);

    const effectiveScope = instructorMode ? "course" : scope;

    if (!title.trim() || !displayName.trim() || !content.trim()) {
      setError("Título, nombre visible y contenido son obligatorios.");
      setIsSubmitting(false);
      return;
    }

    if (effectiveScope === "course" && !courseId) {
      setError("Selecciona un curso para comunicados por curso.");
      setIsSubmitting(false);
      return;
    }

    try {
      const extras = buildExtrasPayload();
      const isEditing = Boolean(editingId);
      const res = await fetch(
        isEditing ? `${API_URL}/announcements/${editingId}` : `${API_URL}/announcements`,
        {
          method: isEditing ? "PATCH" : "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: title.trim(),
            displayName: displayName.trim(),
            content: content.trim(),
            scope: effectiveScope,
            courseId: effectiveScope === "course" ? courseId : undefined,
            ...extras,
          }),
        },
      );

      const isJson = res.headers.get("content-type")?.includes("application/json");
      const data = isJson ? await res.json() : {};

      if (!res.ok) {
        setError(data.error || (isEditing ? "Error al actualizar el borrador." : "Error al crear el comunicado."));
        return;
      }

      setMensaje(isEditing ? getMessage("announcements.draftUpdated") : "Comunicado guardado como borrador.");
      clearForm();
      await loadAnnouncements();
    } catch {
      setError(editingId ? "Error de conexión al actualizar el borrador." : "Error de conexión al crear el comunicado.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVolver = () => {
    navigate(getHomeRouteByRole(rol), { replace: true });
  };

  const handleToggleSelected = (announcementId, isChecked) => {
    setSelectedIds((prev) => toggleAnnouncementSelection(announcementId, isChecked, prev));
  };

  const handleToggleSelectAll = () => {
    setSelectedIds((prev) => toggleAllAnnouncementSelection(visibleAnnouncements, prev));
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      setError("Selecciona al menos un comunicado para eliminar.");
      return;
    }
    const confirmed = globalThis.confirm(
      `¿Eliminar ${selectedIds.length} comunicado(s) seleccionado(s)? Esta acción no se puede deshacer.`,
    );
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    setMensaje("");
    setError("");
    try {
      const res = await fetch(`${API_URL}/announcements/bulk`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "No se pudieron eliminar los comunicados.");
        return;
      }
      setMensaje(`${data.deletedCount ?? selectedIds.length} comunicado(s) eliminado(s).`);
      if (editingId && selectedIds.includes(editingId)) {
        clearForm();
      }
      setSelectedIds([]);
      await loadAnnouncements();
    } catch {
      setError("Error de conexión al eliminar comunicados.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePublishAnnouncement = async (announcementId) => {
    if (!announcementId) return;
    setMensaje("");
    setError("");
    try {
      const res = await fetch(`${API_URL}/announcements/${announcementId}/publish`, {
        method: "PATCH",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "No se pudo publicar el comunicado.");
        return;
      }
      setMensaje("Comunicado publicado correctamente.");
      if (String(announcementId) === String(editingId)) {
        clearForm();
      }
      await loadAnnouncements();
    } catch {
      setError("Error de conexión al publicar el comunicado.");
    }
  };

  const updateLinkRow = (index, field, value) => {
    setLinks((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const updateAttachmentRow = (index, field, value) => {
    setAttachments((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  return (
    <div className="gd-container ga-page">
      <div className="gd-main-container">
        <div className="gd-content">
          <div className={`titulo-director${instructorMode ? " titulo-instructor" : ""}`}>
            <h1 className="texto-director">Gestión de comunicados</h1>
          </div>

          <p className="ga-description">
            {instructorMode
              ? "Publica comunicados para los alumnos de tus cursos asignados. Puedes añadir enlaces y archivos (URL https)."
              : "Los estudiantes solo pueden leer los comunicados. Envía comunicados globales o por curso."}
          </p>

          <form onSubmit={handleSubmit} className="ga-form">
            <h3 ref={editorHeadingRef}>
              {editingId
                ? getMessage("announcements.editingHeading")
                : getMessage("announcements.newHeading")}
            </h3>

            <div className="ga-field">
              <label htmlFor="ga-title">Título *</label>
              <input
                id="ga-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Título del comunicado"
                className="ga-input"
                maxLength={200}
              />
            </div>

            <div className="ga-field">
              <label htmlFor="ga-display-name">Nombre visible para el alumno *</label>
              <input
                id="ga-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ej. Dirección académica, Secretaría o Tutoría"
                className="ga-input"
                maxLength={80}
                required
              />
            </div>

            <div className="ga-field">
              <label htmlFor="ga-content">Contenido *</label>
              <textarea
                id="ga-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Escribe el mensaje..."
                className="ga-textarea"
                rows={5}
                maxLength={5000}
              />
            </div>

            {!instructorMode ? (
              <div className="ga-field">
                <label htmlFor="ga-scope">Alcance</label>
                <ViewportSelect
                  id="ga-scope"
                  value={scope}
                  onChange={(e) => {
                    setScope(e.target.value);
                    if (e.target.value === "global") setCourseId("");
                  }}
                  className="ga-select"
                >
                  <option value="global">Global (todos los estudiantes)</option>
                  <option value="course">Por curso</option>
                </ViewportSelect>
              </div>
            ) : null}

            {(instructorMode || scope === "course") && (
              <div className="ga-field">
                <label htmlFor="ga-course">Curso</label>
                <ViewportSelect
                  id="ga-course"
                  value={courseId}
                  onChange={(e) => setCourseId(e.target.value)}
                  className="ga-select"
                >
                  <option value="">Selecciona un curso</option>
                  {cursos.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.titulo || c.nombre || "(Sin título)"}
                    </option>
                  ))}
                </ViewportSelect>
              </div>
            )}

            <fieldset className="ga-extras-fieldset">
              <legend>Enlaces (opcional)</legend>
              {links.map((link, index) => (
                <div key={`link-${index}`} className="ga-extras-row">
                  <input
                    type="text"
                    className="ga-input"
                    placeholder="Texto del enlace"
                    value={link.label}
                    onChange={(e) => updateLinkRow(index, "label", e.target.value)}
                  />
                  <input
                    type="url"
                    className="ga-input"
                    placeholder="https://..."
                    value={link.url}
                    onChange={(e) => updateLinkRow(index, "url", e.target.value)}
                  />
                </div>
              ))}
              <button
                type="button"
                className="ga-btn-secondary"
                onClick={() => setLinks((prev) => [...prev, { ...EMPTY_LINK }])}
              >
                Añadir enlace
              </button>
            </fieldset>

            <fieldset className="ga-extras-fieldset">
              <legend>Adjuntos por URL (opcional)</legend>
              {attachments.map((file, index) => (
                <div key={`att-${index}`} className="ga-extras-row ga-extras-row--stack">
                  <input
                    type="text"
                    className="ga-input"
                    placeholder="Nombre del archivo"
                    value={file.filename}
                    onChange={(e) => updateAttachmentRow(index, "filename", e.target.value)}
                  />
                  <input
                    type="url"
                    className="ga-input"
                    placeholder="https://..."
                    value={file.url}
                    onChange={(e) => updateAttachmentRow(index, "url", e.target.value)}
                  />
                  <input
                    type="text"
                    className="ga-input"
                    placeholder="Tipo MIME (opcional)"
                    value={file.mimeType}
                    onChange={(e) => updateAttachmentRow(index, "mimeType", e.target.value)}
                  />
                </div>
              ))}
              <button
                type="button"
                className="ga-btn-secondary"
                onClick={() => setAttachments((prev) => [...prev, { ...EMPTY_ATTACHMENT }])}
              >
                Añadir adjunto
              </button>
            </fieldset>

            {(error || mensaje) && (
              <div ref={feedbackRef}>
                {error && <p className="ga-error" role="alert">{error}</p>}
                {mensaje && <p className="ga-success" role="status">{mensaje}</p>}
              </div>
            )}

            <div className="ga-form-actions">
              <button
                type="submit"
                className="ga-btn-submit"
                disabled={!isRoleLoaded || !rol || isRoleError || isSubmitting}
              >
                {isRoleError
                  ? "Sesión no verificada"
                  : !isRoleLoaded
                    ? "Comprobando permisos…"
                    : editingId
                      ? getMessage("announcements.saveDraftChanges")
                      : "Guardar borrador"}
              </button>
              {editingId ? (
                <button
                  type="button"
                  className="ga-btn-secondary"
                  onClick={clearForm}
                  disabled={isSubmitting}
                >
                  {getMessage("announcements.cancelEdit")}
                </button>
              ) : null}
            </div>
          </form>

          <section className="ga-list-section">
            <div className="ga-list-toolbar">
              <h3>Comunicados enviados</h3>
              <div className="ga-list-toolbar-actions">
                <AnnouncementCourseFilter
                  value={listCourseFilter}
                  courses={cursos}
                  onChange={setListCourseFilter}
                />
                {visibleAnnouncements.length > 0 ? (
                  <>
                    <label className="ga-select-all">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === visibleAnnouncements.length}
                        onChange={handleToggleSelectAll}
                      />
                      Seleccionar todos
                    </label>
                    <button
                      type="button"
                      className="ga-btn-delete"
                      onClick={handleDeleteSelected}
                      disabled={isDeleting || selectedIds.length === 0}
                    >
                      {isDeleting
                        ? "Eliminando…"
                        : `Eliminar seleccionados (${selectedIds.length})`}
                    </button>
                  </>
                ) : null}
              </div>
            </div>
            {visibleAnnouncements.length === 0 ? (
              <p className="ga-empty">
                {listCourseFilter
                  ? getMessage("announcements.emptyFiltered")
                  : getMessage("announcements.emptyList")}
              </p>
            ) : (
              <ul className="ga-list">
                {visibleAnnouncements.map((a) => (
                  <li key={a._id} className="ga-list-item">
                    <label className="ga-item-select">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(a._id)}
                        onChange={(e) => handleToggleSelected(a._id, e.target.checked)}
                      />
                    </label>
                    <div className="ga-item-body">
                    <div className="ga-item-header">
                      <strong className="ga-item-title">{a.title}</strong>
                      <span className="ga-item-scope">
                        {a.scope === "global" ? "Global" : a.courseId?.titulo || "Curso"}
                      </span>
                      <span className={`ga-item-status ga-item-status--${a.status === "draft" ? "draft" : "published"}`}>
                        {a.status === "draft" ? "Borrador" : "Publicado"}
                      </span>
                    </div>
                    <p className="ga-item-preview">{a.content}</p>
                    <details className="ga-item-full">
                      <summary>Ver comunicado completo</summary>
                      <p>{a.content}</p>
                      <AnnouncementInteractiveResources
                        announcementId={a._id}
                        links={a.links}
                        attachments={a.attachments}
                      />
                    </details>
                    <div className="ga-item-footer">
                      <div className="ga-item-footer-meta">
                        {Array.isArray(a.attachments) && a.attachments.length > 0 ? (
                          <p className="ga-item-meta">{a.attachments.length} adjunto(s)</p>
                        ) : null}
                        <span className="ga-item-date">
                          {formatRelativeDate(a.createdAt)} {a.displayName && `· ${a.displayName}`}
                        </span>
                      </div>
                      {a.status === "draft" ? (
                        <div className="ga-item-actions">
                          <button
                            type="button"
                            className="ga-btn-edit"
                            aria-label={getMessage("announcements.editDraftAria", { title: a.title })}
                            onClick={() => handleStartEdit(a)}
                          >
                            {getMessage("announcements.editDraft")}
                          </button>
                          <button
                            type="button"
                            className="ga-btn-publish"
                            onClick={() => handlePublishAnnouncement(a._id)}
                          >
                            Publicar comunicado
                          </button>
                        </div>
                      ) : null}
                    </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <ResponsiveActionSidebar
          id="gestion-anuncios-sidebar"
          className="gd-sidebar"
          openClassName="gd-sidebar--open"
          ariaLabel="Acciones de comunicados"
        >
          <h4>Acciones</h4>
          <ul className="gd-links">
            <li className="gd-link-item">
              <button
                type="button"
                className="gd-link staff-sidebar-exit-button"
                onClick={handleVolver}
              >
                Volver
              </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
      </div>
    </div>
  );
};

export default GestionAnuncios;
